const SPOTIFY_API_BASE = 'https://api.spotify.com/v1';
const SPOTIFY_TOKEN_ENDPOINT = 'https://accounts.spotify.com/api/token';

const clientId = import.meta.env.VITE_SPOTIFY_CLIENT_ID;
const clientSecret = import.meta.env.VITE_SPOTIFY_CLIENT_SECRET;
const tokenProxy = import.meta.env.VITE_SPOTIFY_TOKEN_PROXY_URL;

// Comprehensive debug logging in development
if (import.meta.env.DEV) {
  console.group('[Spotify] Environment Configuration');
  console.log('VITE_SPOTIFY_TOKEN_PROXY_URL:', {
    raw: import.meta.env.VITE_SPOTIFY_TOKEN_PROXY_URL,
    type: typeof import.meta.env.VITE_SPOTIFY_TOKEN_PROXY_URL,
    truthy: !!import.meta.env.VITE_SPOTIFY_TOKEN_PROXY_URL,
    length: import.meta.env.VITE_SPOTIFY_TOKEN_PROXY_URL?.length,
  });
  console.log('tokenProxy variable:', {
    value: tokenProxy,
    type: typeof tokenProxy,
    truthy: !!tokenProxy,
    undefined: tokenProxy === undefined,
    null: tokenProxy === null,
    emptyString: tokenProxy === '',
  });
  console.log('VITE_SPOTIFY_CLIENT_ID:', {
    raw: import.meta.env.VITE_SPOTIFY_CLIENT_ID,
    hasValue: !!import.meta.env.VITE_SPOTIFY_CLIENT_ID,
  });
  console.log('VITE_SPOTIFY_CLIENT_SECRET:', {
    raw: import.meta.env.VITE_SPOTIFY_CLIENT_SECRET ? '[REDACTED]' : undefined,
    hasValue: !!import.meta.env.VITE_SPOTIFY_CLIENT_SECRET,
  });
  console.log('All VITE_ env vars:', Object.keys(import.meta.env).filter(key => key.startsWith('VITE_')));
  console.log('import.meta.env keys:', Object.keys(import.meta.env));
  console.groupEnd();
}

interface SpotifyTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export interface SpotifyArtist {
  id: string;
  name: string;
  external_urls?: {
    spotify?: string;
  };
}

export interface SpotifyAlbum {
  id: string;
  name: string;
  images: Array<{ url: string; height: number; width: number }>;
  external_urls: {
    spotify?: string;
  };
}

export interface SpotifyTrack {
  id: string;
  name: string;
  artists: SpotifyArtist[];
  album: SpotifyAlbum;
  duration_ms: number;
  external_urls: {
    spotify: string;
  };
  preview_url: string | null;
  external_ids?: {
    isrc?: string;
  };
}

export interface SimplifiedSpotifyTrack {
  id: string;
  name: string;
  artists: string[];
  albumName: string;
  albumImage?: string;
  durationMs: number;
  previewUrl?: string | null;
  externalUrl: string;
  isrc?: string;
}

let cachedToken: {
  accessToken: string;
  expiresAt: number;
} | null = null;

async function requestSpotifyToken(): Promise<string> {
  console.log('[Spotify] requestSpotifyToken called');
  console.log('[Spotify] Cached token check:', {
    hasCachedToken: !!cachedToken,
    expiresAt: cachedToken?.expiresAt,
    now: Date.now(),
    isExpired: cachedToken ? cachedToken.expiresAt <= Date.now() : true,
  });

  if (cachedToken && cachedToken.expiresAt > Date.now()) {
    console.log('[Spotify] Using cached token');
    return cachedToken.accessToken;
  }

  console.log('[Spotify] Need to fetch new token');
  console.log('[Spotify] tokenProxy check:', {
    tokenProxy,
    type: typeof tokenProxy,
    truthy: !!tokenProxy,
    undefined: tokenProxy === undefined,
    null: tokenProxy === null,
    emptyString: tokenProxy === '',
    stringLength: typeof tokenProxy === 'string' ? tokenProxy.length : 'N/A',
  });

  // Auto-detect proxy URL if not set (for Vercel deployments)
  let proxyUrl = tokenProxy;
  if (!proxyUrl && typeof window !== 'undefined') {
    // In production, use the same origin for the API endpoint
    proxyUrl = `${window.location.origin}/api/spotify-token`;
    console.log('[Spotify] Auto-detected proxy URL:', proxyUrl);
  }

  if (proxyUrl) {
    console.log('[Spotify] Using token proxy:', proxyUrl);
    try {
      console.log('[Spotify] Fetching from proxy:', proxyUrl);
      const response = await fetch(proxyUrl);
      console.log('[Spotify] Proxy response:', {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
      });
      
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.error('[Spotify] Proxy error response:', errorText);
        throw new Error(`Failed to fetch Spotify token from proxy (${response.status}): ${errorText}`);
      }
      
      const payload = (await response.json()) as { access_token: string; expires_in: number };
      console.log('[Spotify] Token received:', {
        hasAccessToken: !!payload.access_token,
        expiresIn: payload.expires_in,
        tokenLength: payload.access_token?.length,
      });
      
      cachedToken = {
        accessToken: payload.access_token,
        expiresAt: Date.now() + (payload.expires_in - 60) * 1000,
      };
      console.log('[Spotify] Token cached until:', new Date(cachedToken.expiresAt).toISOString());
      return cachedToken.accessToken;
    } catch (error) {
      console.error('[Spotify] Proxy fetch error:', error);
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.error('[Spotify] Network error - proxy not reachable');
        throw new Error(
          'Cannot connect to Spotify token proxy. Make sure the proxy server is running (npm run spotify-proxy) and accessible at ' +
            proxyUrl
        );
      }
      throw error;
    }
  }

  console.log('[Spotify] No token proxy, checking client credentials');
  console.log('[Spotify] Client credentials check:', {
    hasClientId: !!clientId,
    hasClientSecret: !!clientSecret,
    clientIdType: typeof clientId,
    clientSecretType: typeof clientSecret,
  });

  if (!clientId || !clientSecret) {
    console.error('[Spotify] No credentials configured');
    throw new Error(
      'Spotify credentials are not configured. Set VITE_SPOTIFY_TOKEN_PROXY_URL in .env.local (and restart dev server) or set VITE_SPOTIFY_CLIENT_ID and VITE_SPOTIFY_CLIENT_SECRET.'
    );
  }

  const credentials = btoa(`${clientId}:${clientSecret}`);
  const response = await fetch(SPOTIFY_TOKEN_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: `Basic ${credentials}`,
    },
    body: new URLSearchParams({
      grant_type: 'client_credentials',
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to retrieve Spotify access token');
  }

  const payload = (await response.json()) as SpotifyTokenResponse;
  cachedToken = {
    accessToken: payload.access_token,
    expiresAt: Date.now() + (payload.expires_in - 60) * 1000,
  };
  return cachedToken.accessToken;
}

async function spotifyFetch<T>(path: string, params?: Record<string, string>): Promise<T> {
  console.log('[Spotify] spotifyFetch called:', { path, params });
  console.log('[Spotify] Requesting token...');
  const token = await requestSpotifyToken();
  console.log('[Spotify] Token received, length:', token?.length);
  
  const url = new URL(`${SPOTIFY_API_BASE}${path}`);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, value);
      }
    });
  }

  console.log('[Spotify] Fetching from Spotify API:', url.toString());
  const response = await fetch(url.toString(), {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  console.log('[Spotify] API response:', {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    console.error('[Spotify] API error response:', errorText);
    throw new Error(`Spotify request failed: ${response.status} - ${errorText}`);
  }

  const data = await response.json() as T;
  console.log('[Spotify] API response data received');
  return data;
}

export async function searchSpotifyTracks(query: string, limit = 6): Promise<SimplifiedSpotifyTrack[]> {
  console.log('[Spotify] searchSpotifyTracks called:', { query, limit });
  
  if (!query.trim()) {
    console.log('[Spotify] Empty query, returning empty array');
    return [];
  }

  try {
    console.log('[Spotify] Starting search...');
    const payload = await spotifyFetch<{ tracks: { items: SpotifyTrack[] } }>('/search', {
      q: query,
      type: 'track',
      limit: String(limit),
    });

    console.log('[Spotify] Search results:', {
      totalItems: payload.tracks.items.length,
      items: payload.tracks.items.map(t => ({ name: t.name, artists: t.artists.map(a => a.name) })),
    });

    const mapped = payload.tracks.items.map(mapTrack);
    console.log('[Spotify] Mapped results:', mapped.length, 'tracks');
    return mapped;
  } catch (error) {
    console.error('[Spotify] searchSpotifyTracks error:', error);
    throw error;
  }
}

export function extractSpotifyTrackId(input: string): string | null {
  if (!input) return null;

  const trimmed = input.trim();
  if (trimmed.includes('open.spotify.com/track/')) {
    const url = new URL(trimmed.includes('http') ? trimmed : `https://${trimmed}`);
    const parts = url.pathname.split('/');
    const id = parts[parts.length - 1];
    return id?.split('?')[0] ?? null;
  }

  if (trimmed.startsWith('spotify:track:')) {
    return trimmed.split(':')[2] ?? null;
  }

  if (/^[0-9A-Za-z]{22}$/.test(trimmed)) {
    return trimmed;
  }

  return null;
}

export async function getSpotifyTrackById(id: string): Promise<SimplifiedSpotifyTrack | null> {
  if (!id) return null;
  const track = await spotifyFetch<SpotifyTrack>(`/tracks/${id}`);
  return mapTrack(track);
}

export async function getSpotifyTrackFromInput(input: string): Promise<SimplifiedSpotifyTrack | null> {
  const id = extractSpotifyTrackId(input);
  if (!id) {
    return null;
  }
  return getSpotifyTrackById(id);
}

function mapTrack(track: SpotifyTrack): SimplifiedSpotifyTrack {
  return {
    id: track.id,
    name: track.name,
    artists: track.artists.map((artist) => artist.name),
    albumName: track.album.name,
    albumImage: track.album.images[0]?.url,
    durationMs: track.duration_ms,
    previewUrl: track.preview_url,
    externalUrl: track.external_urls.spotify,
    isrc: track.external_ids?.isrc,
  };
}

